import { motion } from 'framer-motion';
import { useRef, useEffect, useState, useMemo } from 'react';

import Image from '@atoms/Image';
import Button from '@atoms/new/button';
import HoverBox from '@atoms/HoverBox';
import WheelCarousel from './carousel';
import { rotate } from './helperfunctions';
import useFonts from '@lib/hooks/useFonts';
import { drawWheel, colors as paletteSeed } from './wheel-canvas.service';
import { chromaPaletteParser } from '../helper/chromaPaletteParser';
import { useWheelData } from '@lib/hooks/useWheelData';
import { getCommentsDetailsByFeatureId } from '@services/youtube-platform.service';
import useFontSizeCalculator from '@components/Presentation/helpers/fontSizeCalculator';
import { TrashIcon as DeleteIcon, ChatCircleDotsIcon } from '@phosphor-icons/react';

const ResultCanvas = ({
  splitDiv,
  layoutMode,
  playButtonSound,
  setIsResultView,
  isPreview = false,
  showInstantCustomisationPopup = false,
}) => {
  const {
    type,
    color,
    setting,
    streamId,
    metadata,
    wheelState,
    winnerInfo,
    currentRosResult,
    removeParticipant,
    setInteractionState,

    setWinner,
    setWinnerInfo
  } = useWheelData(s => ({
    type: s.type,
    color: s.color,
    setting: s.setting,
    streamId: s.streamId,
    metadata: s.metadata,
    wheelState: s.wheelState,
    winnerInfo: s.winnerInfo,
    currentRosResult: s.currentRosResult,
    removeParticipant: s.removeParticipant,
    setInteractionState: s.setInteractionState,
    setWinner: s.setWinner,
    setWinnerInfo: s.setWinnerInfo,
  }));

  const angle = useRef(0);

  const canvasRef = useRef(null);

  const [radius, setRadius] = useState();
  const [comments, setComments] = useState([]);
  const [canvasSide, setCanvasSide] = useState();
  const [opts, setOpts] = useState(currentRosResult[0]?.options || []);
  const [showComments, setShowComments] = useState(false);

  useEffect(() => {
    setOpts(currentRosResult[0]?.options || []);
  }, [currentRosResult]);

  const pi = Math.PI;
  const sectorAngle = (2 * pi) / opts.length;
  const radiusOffset = layoutMode === 'square' ? 210 : 90;

  let chromaColor;
  let wheelType = 'winningWheelFontSize';
  if (type === 'wheel') {
    chromaColor = color?.choiceCircleBgColor;
    wheelType = 'choiceCircleFontSize';
  } else if (type === 'newWheel') {
    chromaColor = color?.winningWheelBgColor;
    wheelType = 'winningWheelFontSize';
  }

  const { FontStyle } = setting || {};
  const { getFontStyle } = useFonts();
  const { fontFamily } = getFontStyle(FontStyle ?? 'Sans-Serif');
  const computedFontSize = useFontSizeCalculator(setting?.[wheelType] ? Number(setting[wheelType]) : 1, 1, '');

  const textColor = color?.winningWheelHeadingColor || '#ffffff';

  const newColors = useMemo(
    () => chromaPaletteParser().parse(paletteSeed, chromaColor),
    [chromaColor]
  );

  const createWheel = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const isResult = false;
    drawWheel(canvas, radius, opts, canvasSide, isResult, newColors, isPreview, fontFamily, computedFontSize);
  };

  function spinToWinner() {
    const index = opts?.findIndex((sr) => sr.body === winnerInfo?.value);
    const i = opts?.length * 2 - index;
    angle.current = i * sectorAngle - sectorAngle / 2;
    rotate(canvasRef, angle, createWheel);
  }

  useEffect(() => {
    const marginY = 90;
    const hero = document.getElementById('hero');
    const tempCanvasSide = hero.clientHeight - radiusOffset - marginY + 20;

    setCanvasSide(tempCanvasSide > 199 ? tempCanvasSide : 300);
  }, [radiusOffset, showInstantCustomisationPopup]);

  useEffect(() => {
    if (typeof canvasSide !== 'number' || !Number.isFinite(canvasSide)) return;
    setRadius((canvasSide / 2) - 10);
  }, [canvasSide]);

  useEffect(() => {
    if (winnerInfo?.value) {
      if (!opts?.length) return;
      spinToWinner();
    }
  }, [radius, opts, winnerInfo?.value]);

  const getComments = async (streamId, featureId, username) => {
    const tepComments = await getCommentsDetailsByFeatureId(
      streamId,
      featureId,
      username
    );
    if (tepComments?.entity?.rows) {
      setComments(tepComments.entity.rows);
    }
  };

  useEffect(() => {
    if (winnerInfo?.value === null || type !== 'newWheel') return;
    const featureId = (metadata?.featureId || setting?.wheelInteractionId) || streamId;
    getComments(streamId, featureId, winnerInfo?.value);
  }, [winnerInfo?.value, setting?.wheelInteractionId, metadata?.featureId, setting?.enableWheelComments]);

  return <>
    {radius
            && <div className={`flex ${layoutMode === 'square' ? 'flex-col-reverse justify-center' : 'gap-x-[7%]'} items-center w-full h-full`}>
              <div style={{ position: 'relative', width: `${canvasSide}px`, height: `${canvasSide}px` }} >
                <motion.canvas
                  ref={canvasRef}
                  width={canvasSide} height={canvasSide}
                />
                <img style={{ top: `${(radius - 22)}px` }} src='/images/wheel_marker.svg' className='absolute -right-26' />

                <div className='absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 flex flex-col items-center pb-3'>
                  {opts?.length === 0 &&
                            <div className='w-full mt-[140%] text-center'>
                              <p className='text-grey-900 font-semibold text-2xl'>The wheel is empty!</p>
                              <p className='text-grey-800 '>Participate in the chat to appear here</p>
                            </div>}
                  {wheelState === 'spin' || (opts?.length) === 0 ? null : (
                    <Button
                      size="md"
                      kind="custom"
                      disabled={wheelState === 'spin'}
                      className={`mt-4 shadow-lg ${isPreview ? 'w-10 h-20 text-md overflow-hidden' : 'w-24 h-24 text-2xl'} rounded-full shadow-lg bg-white border  animate-scale-pulse
                border-grey-200 hover:bg-grey-200 font-semibold text-grey-900 aria-expanded:bg-grey-200 ${wheelState === 'spin' ? 'bg-grey-200' : 'bg-white'}`}
                      onPress={() => {
                        playButtonSound();
                        setIsResultView(false);
                        setWinner({ value: null });
                        setInteractionState('start');
                        setWinnerInfo({ value: null });
                      }}
                    >
                                Spin
                    </Button>
                  )}
                </div>
              </div>
              {
                !splitDiv &&
                    <div className='w-full h-full flex flex-col justify-center'>
                      <div className={`font-Poppins rounded-2xl relative px-4 py-3 text-center md:w-[55%] ${layoutMode === 'rectangle' ? 'lg:w-[45%] h-fit' : 'h-28 justify-center'} 
          font-medium flex items-center`}>
                        {winnerInfo?.value ?
                          <>
                            {type === 'newWheel' && wheelState !== 'spin' ?
                              <Image className="" src="/images/InteractionPage/crown.svg" />
                              : null}
                            <p className={'ml-[16px] text-4xl font-semibold max-w-full truncate-text text-left'} style={{ color: textColor }}>{winnerInfo?.value}</p>
                          </> : null
                        }
                        {opts?.length > 2 && winnerInfo?.value && wheelState !== 'spin' && !isPreview ?
                          <div className='flex items-center'>
                            {process.env.NEXT_PUBLIC_APP_ENV !== 'sandbox'
                              ? (
                                <div>
                                  <HoverBox
                                    position='top'
                                    variant='white'
                                    id='delete-icon'
                                    handleSvgClick={() => {
                                      removeParticipant();
                                      setIsResultView(false);
                                    }}
                                    toolTipDescription='Remove participant'
                                    svg={<DeleteIcon size={26} weight='bold' color="#1F1F1F" />}
                                    className='group flex items-center justify-center rounded-md w-11 h-11 bg-white shadow-lg cursor-pointer ml-[16px]'
                                  />
                                </div>) : null}
                            {setting?.enableWheelComments && layoutMode !== 'square' && comments?.length > 0 ?
                              <div>
                                <HoverBox
                                  position='top'
                                  variant='white'
                                  id='chat-toggle-icon'
                                  handleSvgClick={() => setShowComments((prev) => !prev)}
                                  toolTipDescription={showComments ? 'Hide comments' : 'Show comments'}
                                  svg={<ChatCircleDotsIcon size={26} weight={showComments ? 'fill' : 'regular'} color="#1F1F1F" />}
                                  className='group flex items-center justify-center rounded-md w-11 h-11 bg-white shadow-lg cursor-pointer ml-[16px]'
                                />
                              </div> : null}
                          </div> : null}
                      </div>
                      {setting?.enableWheelComments && layoutMode !== 'square' && comments?.length > 0 && showComments ?
                        <WheelCarousel textColor={textColor} comments={comments} metadata={metadata} />
                        : <></>}
                    </div>

              }
            </div>
    }
  </>;
};

export default ResultCanvas;
